import {
  Calendar,
  MapPin,
  Users,
  ClipboardList,
  Settings,
  FileText,
  Activity,
  Database,
  CheckCircle2,
  ChevronDown,
  LogOut,
  PanelTop,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
  AlertTriangle,
  History,
  UserCheck,
  Eye,
  Layers,
  Mail,
  DollarSign,
  BookUser,
  Clock,
  CalendarCheck,
  Trophy,
  Plane,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "@/components/NavLink";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationsSidebar } from "@/components/NotificationsSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarSeparator,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { useNavLayout } from "@/hooks/useNavLayout";
import { logAuditEvent } from "@/lib/auditLog";
import { useEnvironment } from "@/contexts/EnvironmentContext";
import { useCoachView } from "@/contexts/CoachViewContext";

interface AppSidebarProps {
  isAdmin: boolean;
  isCoach: boolean;
  isAssociate?: boolean;
  isAssistant?: boolean;
  profile?: any;
  separatorPadding?: string;
}

export function AppSidebar({
  isAdmin,
  isCoach,
  isAssistant = false,
  profile,
  separatorPadding,
}: AppSidebarProps) {
  const { state, toggleSidebar, setOpen } = useSidebar();
  const collapsed = state === "collapsed";
  // Add zero vertical padding by default and keep horizontal padding customizable
  const sepClass = separatorPadding ?? (collapsed ? "py-0 px-1" : "py-0 px-4");
  const navigate = useNavigate();
  const { showDevData } = useEnvironment();
  const { isAdmin: canUseCoachView, viewAsCoach, toggleViewMode } = useCoachView();
  const coachViewLabel = viewAsCoach ? "Admin View" : "Coach View";
  const GROUPS_STORAGE_KEY = "ms:sidebarGroups";

  const getInitial = (name: string, def: boolean) => {
    try {
      if (typeof window === "undefined") return def;
      const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
      if (!raw) return def;
      const parsed = JSON.parse(raw || "{}");
      return parsed?.[name] ?? def;
    } catch (err) {
      return def;
    }
  };

  const [mainOpen, setMainOpen] = useState<boolean>(() =>
    getInitial("mainOpen", true)
  );
  const [coachOpen, setCoachOpen] = useState<boolean>(() =>
    getInitial("coachOpen", true)
  );
  const [managementOpen, setManagementOpen] = useState<boolean>(() =>
    getInitial("managementOpen", true)
  );
  const [configOpen, setConfigOpen] = useState<boolean>(() =>
    getInitial("configOpen", true)
  );

  // Refs for measuring group content heights so we can animate to the actual height
  const mainRef = useRef<HTMLDivElement | null>(null);
  const coachRef = useRef<HTMLDivElement | null>(null);
  const managementRef = useRef<HTMLDivElement | null>(null);
  const configRef = useRef<HTMLDivElement | null>(null);

  const setMeasuredHeight = (el: HTMLDivElement | null, open: boolean) => {
    if (!el) return;
    try {
      // if this is the first time we touch the element, set initial styles instantly and skip animation
      if (!el.dataset?.sbInit) {
        el.style.maxHeight = open ? "none" : "0px";
        el.style.opacity = open ? "1" : "0";
        el.style.transform = open
          ? "translateY(0) scale(1)"
          : "translateY(-8px) scale(.95)";
        el.dataset.sbInit = "1";
        return;
      }
      // When opening: set max-height to the element's scrollHeight so it animates to the correct size
      if (open) {
        el.style.maxHeight = `${el.scrollHeight}px`;
        el.style.opacity = "1";
        el.style.transform = "translateY(0) scale(1)";
        // after the transition completes, remove the max-height so it can naturally size on changes
        const clear = () => {
          el.style.maxHeight = "none";
          el.removeEventListener("transitionend", clear);
        };
        el.addEventListener("transitionend", clear);
      } else {
        // Closing: set max-height to current height, force a reflow and then set to 0
        el.style.maxHeight = `${el.scrollHeight}px`;
        // force reflow
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.offsetHeight;
        el.style.opacity = "0";
        el.style.transform = "translateY(-8px) scale(.95)";
        el.style.maxHeight = "0px";
      }
    } catch (err) {
      // fall back to our CSS classes if anything fails
    }
  };

  // when each group's open state toggles, animate the content to measured height
  useEffect(() => setMeasuredHeight(mainRef.current, mainOpen), [mainOpen]);
  useEffect(() => setMeasuredHeight(coachRef.current, coachOpen), [coachOpen]);
  useEffect(
    () => setMeasuredHeight(managementRef.current, managementOpen),
    [managementOpen]
  );
  useEffect(
    () => setMeasuredHeight(configRef.current, configOpen),
    [configOpen]
  );

  // persist group states when they change
  useEffect(() => {
    try {
      const payload = { mainOpen, coachOpen, managementOpen, configOpen };
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // ignore
    }
  }, [mainOpen, coachOpen, managementOpen, configOpen]);

  // sync across tabs/windows
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== GROUPS_STORAGE_KEY) return;
      try {
        const parsed = JSON.parse(e.newValue || "{}");
        if (parsed?.mainOpen !== undefined)
          setMainOpen(Boolean(parsed.mainOpen));
        if (parsed?.coachOpen !== undefined)
          setCoachOpen(Boolean(parsed.coachOpen));
        if (parsed?.managementOpen !== undefined)
          setManagementOpen(Boolean(parsed.managementOpen));
        if (parsed?.configOpen !== undefined)
          setConfigOpen(Boolean(parsed.configOpen));
      } catch (err) {
        /* ignore malformed data */
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const { toggleLayout } = useNavLayout();

  const handleLogout = async () => {
    await logAuditEvent({
      action: "User logged out",
      category: "authentication",
      metadata: { userName: profile?.name },
    });
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const mainItems = [{ title: "Dashboard", url: "/dashboard", icon: Calendar }];

  const managementItems = [
    { title: "Venues", url: "/venues", icon: MapPin },
    { title: "Sessions", url: "/sessions", icon: Calendar },
    { title: "Bulk Sessions", url: "/bulk-sessions", icon: Layers },
    {
      title: "Attendance Approval",
      url: "/attendance-approval",
      icon: CheckCircle2,
    },
    { title: "Unpaid Attendance", url: "/unpaid-attendance", icon: DollarSign },
    { title: "Timesheet Approvals", url: "/timesheets/approvals", icon: Clock },
    { title: "Planner Manager", url: "/planners/admin", icon: CalendarCheck },
    { title: "Holidays", url: "/holidays", icon: Plane },
    { title: "Tournament Approvals", url: "/tournaments/approvals", icon: Trophy },
    { title: "Participant Book", url: "/participant-book", icon: BookUser },
    { title: "Incidents", url: "/incidents", icon: AlertTriangle },
    { title: "Mailshot", url: "/mailshot", icon: Mail },
    { title: "Coach Documents", url: "/coach-documents", icon: FileText },
    { title: "Audit Log", url: "/audit-log", icon: History },
  ];

  const configItems = [
    { title: "Users", url: "/users", icon: Users },
    { title: "Data Sync", url: "/data-sync", icon: Database },
    { title: "Settings", url: "/settings", icon: Settings },
  ];

  const coachItems = isAssistant && !isCoach && !isAdmin
    ? [
        { title: "Sessions", url: "/sessions", icon: Calendar },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ]
    : [
        { title: "Attendance", url: "/attendance", icon: ClipboardList },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Tournaments", url: "/tournaments", icon: Trophy },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ];

  return (
    <Sidebar
      collapsible="icon"
      className="shadow-xl dark:shadow-[var(--sidebar-shadow)]"
    >
      {/* Sticky logo/collapse toggle at top */}

      <div className="sticky top-0 z-10 bg-sidebar border-b border-accent flex items-center justify-between px-3 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-2">
        <NavLink
          to="/"
          className="h-16 w-44 flex items-center justify-start group-data-[collapsible=icon]:hidden"
        >
          <img
            src="/logo_light.webp"
            alt="MicroSports"
            className="block dark:hidden h-16 object-contain"
          />
          <img
            src="/logo_dark.webp"
            alt="MicroSports"
            className="hidden dark:block h-16 object-contain"
          />
        </NavLink>
        {showDevData && !collapsed && (
          <Badge
            variant="outline"
            className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700 group-data-[collapsible=icon]:hidden"
          >
            DEV
          </Badge>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 my-5"
              onClick={toggleSidebar}
            >
              <PanelLeftOpen className="h-4 w-4 hidden group-data-[collapsible=icon]:block" />
              <PanelLeftClose className="h-4 w-4 block group-data-[collapsible=icon]:hidden" />
              <span className="sr-only">Toggle sidebar</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Toggle sidebar</TooltipContent>
        </Tooltip>
      </div>

      <SidebarContent className="flex flex-col ">
        {/* Main navigation {collapsed ? "" : <SidebarSeparator />}  */}
        <SidebarGroup className="pt-2 px-3">
          <div className="mb-0 relative bg-background/30 flex items-start justify-between mt-2 border-b border-accent pb-1 mb-1 group-data-[collapsible=icon]:hidden">
            <div>
              <SidebarGroupLabel className="mb-1">Main</SidebarGroupLabel>
              <p className="text-xs text-muted-foreground px-2 -mt-2">
                Overview & dashboard
              </p>
            </div>
            <SidebarGroupAction
              onClick={() => setMainOpen((s) => !s)}
              aria-expanded={mainOpen}
              title={mainOpen ? "Collapse" : "Expand"}
              className="ml-2"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-300 ${
                  mainOpen ? "rotate-180" : ""
                }`}
              />
            </SidebarGroupAction>
          </div>
          <SidebarGroupContent
            ref={mainRef}
            className={`px-1 transition-[max-height,opacity,transform] duration-300 ease-in-out overflow-hidden group-data-[collapsible=icon]:mt-3 group-data-[collapsible=icon]:mb-2 group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:scale-100 group-data-[collapsible=icon]:pointer-events-auto ${
              mainOpen
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
            }`}
          >
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end
                      className="group/navlink rounded-sm hover:bg-accent hover:text-accent-foreground pl-1 [&>svg]:text-accent group-data-[collapsible=icon]:pl-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>svg]:text-muted-foreground group-data-[collapsible=icon]:text-muted-foreground [&.text-accent-foreground>svg]:!text-accent-foreground hover:[&>svg]:!text-accent-foreground"
                      activeClassName="bg-accent text-accent-foreground font-medium"
                    >
                      <item.icon className="h-4 w-4 shrink-0 transition-colors group-hover/navlink:!text-accent-foreground" />
                      <span className="group-data-[collapsible=icon]:hidden">
                        {item.title}
                      </span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator className="hidden group-data-[collapsible=icon]:block my-2" />

        {(isCoach || isAdmin || isAssistant) && (
          <>
            <SidebarGroup className="pt-0 px-3">
              <div className="mb-0 relative bg-background/30 flex items-start justify-between border-b border-accent pb-1 mb-1 group-data-[collapsible=icon]:hidden">
                <div>
                  <SidebarGroupLabel className="mb-0 mt-0">
                    Coach
                  </SidebarGroupLabel>
                  <p className="text-xs text-muted-foreground mb-1 px-2 -mt-2">
                    Session management
                  </p>
                </div>
                <SidebarGroupAction
                  onClick={() => setCoachOpen((s) => !s)}
                  aria-expanded={coachOpen}
                  title={coachOpen ? "Collapse" : "Expand"}
                  className="ml-2"
                >
                  <ChevronDown
                    className={`h-3 w-3 transition-transform duration-300 ${
                      coachOpen ? "rotate-180" : ""
                    }`}
                  />
                </SidebarGroupAction>
              </div>
              <SidebarGroupContent
                ref={coachRef}
                className={`px-1 transition-[max-height,opacity,transform] duration-300 ease-in-out overflow-hidden group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:scale-100 group-data-[collapsible=icon]:pointer-events-auto ${
                  coachOpen
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                }`}
              >
                <SidebarMenu>
                  {coachItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="group/navlink rounded-sm hover:bg-accent hover:text-accent-foreground pl-1 [&>svg]:text-accent group-data-[collapsible=icon]:pl-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>svg]:text-muted-foreground group-data-[collapsible=icon]:text-muted-foreground [&.text-accent-foreground>svg]:!text-accent-foreground hover:[&>svg]:!text-accent-foreground"
                          activeClassName="bg-accent text-accent-foreground font-medium"
                        >
                          <item.icon className="h-4 w-4 shrink-0 transition-colors group-hover/navlink:!text-accent-foreground" />
                          <span className="group-data-[collapsible=icon]:hidden">
                            {item.title}
                          </span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="hidden group-data-[collapsible=icon]:block my-2" />
          </>
        )}

        {isAdmin && (
          <>
            <SidebarGroup className="pt-0 px-3">
              <div className="mb-0 bg-background/30 relative flex items-start justify-between border-b border-accent pb-1 mb-1 group-data-[collapsible=icon]:hidden">
                <div>
                  <SidebarGroupLabel className="mb-0 mt-0">
                    Management
                  </SidebarGroupLabel>
                  <p className="text-xs text-muted-foreground mb-1 px-2 -mt-2">
                    Records & operations
                  </p>
                </div>
                <SidebarGroupAction
                  onClick={() => setManagementOpen((s) => !s)}
                  aria-expanded={managementOpen}
                  title={managementOpen ? "Collapse" : "Expand"}
                  className="ml-2"
                >
                  <ChevronDown
                    className={`h-3 w-3 transition-transform duration-300 ${
                      managementOpen ? "rotate-180" : ""
                    }`}
                  />
                </SidebarGroupAction>
              </div>
              <SidebarGroupContent
                ref={managementRef}
                className={`px-1 transition-[max-height,opacity,transform] duration-300 ease-in-out overflow-hidden group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:scale-100 group-data-[collapsible=icon]:pointer-events-auto ${
                  managementOpen
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                }`}
              >
                <SidebarMenu>
                  {managementItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="group/navlink rounded-sm hover:bg-accent hover:text-accent-foreground pl-1 [&>svg]:text-accent group-data-[collapsible=icon]:pl-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>svg]:text-muted-foreground group-data-[collapsible=icon]:text-muted-foreground [&.text-accent-foreground>svg]:!text-accent-foreground hover:[&>svg]:!text-accent-foreground"
                          activeClassName="bg-accent text-accent-foreground font-medium"
                        >
                          <item.icon className="h-4 w-4 shrink-0 transition-colors group-hover/navlink:!text-accent-foreground" />
                          <span className="group-data-[collapsible=icon]:hidden">
                            {item.title}
                          </span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="hidden group-data-[collapsible=icon]:block my-2" />
          </>
        )}

        {/* Configuration group - Admin only */}
        {isAdmin && (
          <>
            <SidebarGroup className="pt-0 px-3">
              <div className="mb-0 bg-background/30 relative flex items-start justify-between border-b border-accent pb-1 mb-1 group-data-[collapsible=icon]:hidden">
                <div>
                  <SidebarGroupLabel className="mb-0 mt-0">
                    Configuration
                  </SidebarGroupLabel>
                  <p className="text-xs text-muted-foreground mb-1 px-2 -mt-2">
                    App settings
                  </p>
                </div>
                <SidebarGroupAction
                  onClick={() => setConfigOpen((s) => !s)}
                  aria-expanded={configOpen}
                  title={configOpen ? "Collapse" : "Expand"}
                  className="ml-2"
                >
                  <ChevronDown
                    className={`h-3 w-3 transition-transform duration-300 ${
                      configOpen ? "rotate-180" : ""
                    }`}
                  />
                </SidebarGroupAction>
              </div>
              <SidebarGroupContent
                ref={configRef}
                className={`px-1 transition-[max-height,opacity,transform] duration-300 ease-in-out overflow-hidden group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:scale-100 group-data-[collapsible=icon]:pointer-events-auto ${
                  configOpen
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                }`}
              >
                <SidebarMenu>
                  {configItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="group/navlink rounded-sm hover:bg-accent hover:text-accent-foreground pl-1 [&>svg]:text-accent group-data-[collapsible=icon]:pl-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>svg]:text-muted-foreground group-data-[collapsible=icon]:text-muted-foreground [&.text-accent-foreground>svg]:!text-accent-foreground hover:[&>svg]:!text-accent-foreground"
                          activeClassName="bg-accent text-accent-foreground font-medium"
                        >
                          <item.icon className="h-4 w-4 shrink-0 transition-colors group-hover/navlink:!text-accent-foreground" />
                          <span className="group-data-[collapsible=icon]:hidden">
                            {item.title}
                          </span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />
      </SidebarContent>

      {/* Footer with profile, controls, and logout */}
      <SidebarFooter className="border-t border-sidebar-border">
        <div className="flex flex-col gap-2 p-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:p-1">
          {/* Notifications button */}
          <NotificationsSidebar>
            {(hasNotifications) => (
              <Button
                variant={hasNotifications ? "default" : "ghost"}
                className={`w-full justify-start group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 ${
                  hasNotifications
                    ? "bg-orange-500/60 hover:bg-orange-600 text-white"
                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-muted-foreground"
                }`}
              >
                <Bell
                  className={`h-4 w-4 ${
                    hasNotifications ? "text-white" : "text-current"
                  }`}
                />
                <span className="ml-2 group-data-[collapsible=icon]:hidden">
                  Notifications
                </span>
              </Button>
            )}
          </NotificationsSidebar>

          {/* Profile section */}
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink
                to="/profile"
                className="flex items-center gap-2 w-full rounded-md border border-border/50 hover:border-border hover:bg-accent hover:text-accent-foreground transition-colors p-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:border-none"
              >
                <Avatar className="h-8 w-8 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7">
                  <AvatarImage src={profile?.profile_photo || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {(profile?.name || "")
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "AD"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <div className="text-sm font-medium truncate">
                    {profile?.name ?? "User"}
                  </div>
                </div>
              </NavLink>
            </TooltipTrigger>
            <TooltipContent side="right">View Profile</TooltipContent>
          </Tooltip>

          {/* Coach View Toggle (for admins only) */}
          {canUseCoachView && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={viewAsCoach ? "secondary" : "ghost"}
                  className={`w-full justify-start group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 ${
                    viewAsCoach
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-muted-foreground"
                  }`}
                  onClick={toggleViewMode}
                >
                  {viewAsCoach ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <UserCheck className="h-4 w-4" />
                  )}
                  <span className="ml-2 group-data-[collapsible=icon]:hidden">
                    {coachViewLabel}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{coachViewLabel}</TooltipContent>
            </Tooltip>
          )}

          {/* Control buttons */}
          <div className="flex items-center justify-between gap-1 group-data-[collapsible=icon]:flex-col">
            {/* Switch to top nav */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7"
                  onClick={toggleLayout}
                >
                  <PanelTop className="h-4 w-4" />
                  <span className="sr-only">Switch to top navigation</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                Switch to top navigation
              </TooltipContent>
            </Tooltip>

            {/* Theme toggle */}
            <ThemeToggle className="group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7" />

            {/* Logout */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="sr-only">Logout</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Logout</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
